import React, { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { XIcon } from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";
import {
  useEquipmentCategoryStore,
  useEquipmentSubcategoryStore,
  useManufacturerListStore,
} from "../../../store/masterDataStore";
import {
  usePremisesStore,
  useRegionStore,
  useDepartmentStore,
  useOrgUsersStore,
} from "../../../store/store";
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import { useDropzone } from "react-dropzone";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";
import FileUpload from "../../../components/FileUpload/FileUpload";
import axiosInstance from "../../../common/axiosInstance";
import ImportAssetsModal from "../../../components/ImportAssetsModal/ImportAssetsModal";
import {
  createEquipmentManagement,
  getEquipmentManagementById,
  updateEquipmentManagement,
  getAllSuppliers,
} from "../../../service/api";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
import {
  EquipmentCategoryType,
  EquipmentSubcategoryType,
} from "../../../types/api";

interface Supplier {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
}

interface Department {
  id: string;
  departmentId: string;
  departmentName: string;
}

interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}
interface AddEquipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  equipmentCategoryId?: string;
  mode?: "create" | "view" | "update";
  equipmentId?: string;
}

const AddEquipmentModal: React.FC<AddEquipmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode = "create",
  equipmentCategoryId,
  equipmentId,
}) => {
  const [formData, setFormData] = useState({
    equipmentCategoryId: equipmentCategoryId,
    equipmentId: "",
    equipmentName: "",
    category: "", // Changed to categoryId
    subcategory: "", // Changed to subcategoryId
    serialNumber: "",
    manufacturer: "",
    supplier: "",
    purchaseDate: "",
    warrantyExpiryDate: "",
    amcStatus: "",
    department: "",
    condition: "",
    status: "",
    region: "",
    premises: "",
    floor: "",
    responsiblePerson: "",
    maintenanceScheduled: "",
    room: "",
  });

  const [files, setFiles] = useState<File[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    [],
  );
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeValue, setQRCodeValue] = useState("");
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [maxFileSize, setMaxFileSize] = useState<number>(10);

  // Document preview states
  const [fileUrls, setFileUrls] = useState<
    { name: string; url: string; type: string }[]
  >([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileurl, setFileUrl] = useState({
    url: "",
    name: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);

  const { equipmentCategory, fetchAllEquipmentCategory } =
    useEquipmentCategoryStore();
  const { getsignedurl } = S3TempUrl();

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const { departments, fetchAllDepartments } = useDepartmentStore();

  const { equipmentSubcategory, fetchAllEquipmentSubcategory } =
    useEquipmentSubcategoryStore();
  const { manufacturerList, fetchAllManufacturerList } =
    useManufacturerListStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  useEffect(() => {
    if (!orgUsersData.length) fetchOrgUsers();
  }, [fetchOrgUsers]);

  useEffect(() => {
    if (orgUsersData.length > 0) {
      setUsers(orgUsersData);
    }
  }, [orgUsersData]);

  // State for filtered premises, floor options, and filtered subcategories
  const [filteredPremises, setFilteredPremises] = useState<any[]>([]);
  const [floorOptions, setFloorOptions] = useState<string[]>([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState<any[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  // Condition options
  const conditionOptions = ["New", "Good", "Fair", "Needs Repair", "Scrap"];

  // Helper function to get file extension
  const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  // Helper function to check if file can be previewed in browser
  const canPreviewFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return [
      "jpg",
      "jpeg",
      "png",
      "pdf",
      "txt",
      "doc",
      "docx",
      "ppt",
      "pptx",
    ].includes(ext);
  };
  // Function to handle file download

  const generateQRCodeContent = (equipmentData: any) => {
    const selectedCategory = equipmentCategory.find(
      (cat) => cat._id === equipmentData.categoryId,
    );
    return JSON.stringify({
      equipmentId: equipmentData.equipmentId,
      equipmentName: equipmentData.equipmentName,
      serialNumber: equipmentData.serialNumber,
      category: selectedCategory?.categoryName || "",
      purchaseDate: equipmentData.purchaseDate,
    });
  };

  // Function to handle file preview
  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";

    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);

        const s3Key = attachment.url?.split("/").pop();
        if (!s3Key) {
          throw new Error("Invalid file URL");
        }

        const fileurl = await getsignedurl(s3Key, getFileExtension(filename));
        const filobj = {
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        };
        setFileUrl(filobj);
        setIsDocumentViewerOpen(true);
      } catch (err: any) {
        console.error("Error previewing file:", err);
        setErrorMessage("File not found or failed to load");
        toast.error("Failed to preview file");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info(
        "Preview not available for this file type. Use download instead.",
      );
    }
  };

  // Function to close document viewer
  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  // Function to remove existing attachment (for update mode)
  const handleRemoveExistingAttachment = (index: number) => {
    if (mode === "view") return;

    setExistingAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  // Filter premises based on selected region
  useEffect(() => {
    if (formData.region) {
      const premisesInRegion = allPremises.filter(
        (p) => p.region === formData.region,
      );
      setFilteredPremises(premisesInRegion);

      if (
        formData.premises &&
        !premisesInRegion.some((p) => p._id === formData.premises)
      ) {
        setFormData((prev) => ({
          ...prev,
          premisesId: "",
          floor: "",
          room: "",
        }));
      }
    } else {
      setFilteredPremises(allPremises);
    }
  }, [formData.region, allPremises, formData.premises]);

  // Generate floor options when premises changes
  useEffect(() => {
    if (!formData.premises) {
      setFloorOptions([]);
      setFormData((prev) => ({ ...prev, floor: "", room: "" }));
      return;
    }

    const selectedPremise = allPremises.find(
      (p) => p._id === formData.premises,
    );
    if (!selectedPremise?.floor) {
      setFloorOptions([]);
      setFormData((prev) => ({ ...prev, floor: "", room: "" }));
      return;
    }

    const floorsCount = Number(selectedPremise.floor);
    if (isNaN(floorsCount)) {
      setFloorOptions([]);
      setFormData((prev) => ({ ...prev, floor: "", room: "" }));
      return;
    }

    const floors = Array.from(
      { length: floorsCount },
      (_, i) => `Floor ${i + 1}`,
    );
    setFloorOptions(floors);
  }, [formData.premises, allPremises]);

  // Filter subcategories based on selected category
  useEffect(() => {
    if (formData.category) {
      const filtered = equipmentSubcategory.filter(
        (sub: { categoryId: string }) => sub.categoryId === formData.category,
      );
      setFilteredSubcategories(filtered);

      if (
        formData.subcategory &&
        !filtered.some(
          (sub: { categoryId: string }) =>
            sub.categoryId === formData.subcategory,
        )
      ) {
        setFormData((prev) => ({
          ...prev,
          subcategoryId: "",
        }));
      }
    } else {
      setFilteredSubcategories([]);
      setFormData((prev) => ({
        ...prev,
        subcategoryId: "",
      }));
    }
  }, [formData.category, equipmentSubcategory]);

  useEffect(() => {
    if (isOpen) {
      fetchAllManufacturerList();
      fetchSuppliers();
      fetchPremises();
      fetchAllDepartments();
      fetchAllEquipmentCategory();
      fetchAllEquipmentSubcategory();
      if (!regions.length) fetchRegions();

      // Lock body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    // Cleanup function to restore body scroll
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if ((mode === "view" || mode === "update") && equipmentId && isOpen) {
      fetchEquipmentData();
    }
  }, [mode, equipmentId, isOpen]);

  useEffect(() => {
    if (mode === "create" && isOpen) {
      const newUuid = uuidv4();
      const formattedId = `EQP-${newUuid.substring(0, 8).toUpperCase()}`;

      setFormData((prev) => ({
        ...prev,
        equipmentId: formattedId,
      }));
    }
  }, [mode, isOpen]);

  const fetchEquipmentData = async () => {
    if (!equipmentId) {
      toast.error("Equipment ID is required");
      return;
    }

    setLoading(true);
    try {
      const response = await getEquipmentManagementById(equipmentId as string);

      const equipment = response.data.message;
      // Parse department data if it's stored as a string array
      let departmentArray: string[] = [];
      if (equipment.department) {
        if (Array.isArray(equipment.department)) {
          departmentArray = equipment.department;
        } else if (typeof equipment.department === "string") {
          departmentArray = equipment.department.split(",");
        }
      }

      setFormData({
        equipmentCategoryId: equipmentCategoryId,
        equipmentId: equipment.equipmentId || "",
        equipmentName: equipment.equipmentName || "",
        category: equipment.category || "", // Changed to categoryId
        subcategory: equipment.subCategory || "", // Changed to subcategoryId
        serialNumber: equipment.serialNumber || "",
        manufacturer: equipment.manufacturer || "",
        supplier: equipment.supplier || "",
        purchaseDate: equipment.purchaseDate
          ? equipment.purchaseDate.split("T")[0]
          : "",
        warrantyExpiryDate: equipment.warrantyExpiryDate
          ? equipment.warrantyExpiryDate.split("T")[0]
          : "",
        amcStatus: equipment.amcStatus || "",
        department: equipment.department || "",
        condition: equipment.condition || "",
        status: equipment.status || "",
        region: equipment.regionId || "",
        premises: equipment.premisesId || "",
        responsiblePerson: equipment.responsiblePerson || "",
        maintenanceScheduled: equipment.maintenanceScheduled || "",
        floor: equipment.floor || "",
        room: equipment.room || "",
      });

      // Set selected departments for multi-select
      setSelectedDepartments(departmentArray);

      if (equipment.attachments && equipment.attachments.length > 0) {
        const normalizedAttachments = equipment.attachments.map(
          (attachment: any) => {
            if (typeof attachment === "string") {
              return {
                url: attachment,
                filename: attachment.split("/").pop() || "Unknown file",
                uploadedAt: new Date().toISOString(),
              };
            } else {
              return {
                url: attachment.url || attachment,
                filename:
                  attachment.filename ||
                  attachment.name ||
                  attachment.originalName ||
                  attachment.url?.split("/").pop() ||
                  "Unknown file",
                uploadedAt: attachment.uploadedAt || new Date().toISOString(),
              };
            }
          },
        );
        setExistingAttachments(normalizedAttachments);

        const files = normalizedAttachments.map((attachment: any) => {
          const filename =
            attachment.filename ||
            attachment.name ||
            attachment.originalName ||
            attachment.url?.split("/").pop() ||
            "Unknown file";
          return {
            name: filename,
            url: attachment.url,
            type: getFileExtension(filename),
          };
        });
        setFileUrls(files);
      }
    } catch (error: any) {
      toast.error("Failed to fetch equipment data");
      console.error("Error fetching equipment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const response = await getAllSuppliers();
      setSuppliers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Failed to fetch suppliers");
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (mode === "view") return;

    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Reset dependent fields when region or premises changes
      if (name === "regionId") {
        updated.premises = "";
        updated.floor = "";
        updated.room = "";
      } else if (name === "premisesId") {
        updated.floor = "";
        updated.room = "";
      } else if (name === "floor") {
        updated.room = "";
      } else if (name === "categoryId") {
        updated.subcategory = "";
      }

      return updated;
    });
  };

  const handleDepartmentSelectChange = (departmentIds: string[]) => {
    if (mode === "view") return;

    setSelectedDepartments(departmentIds);
    // Store as comma-separated string for backend compatibility
    setFormData((prev) => ({
      ...prev,
      department: departmentIds.join(","),
    }));
  };

  const handleFileChange = async (acceptedFiles: File[]) => {
    if (mode === "view") return;

    const maxTotalSize = maxFileSize * 1024 * 1024; // 10MB total limit in bytes (can be made dynamic)
    const existingFiles = files || [];
    const existingTotalSize = existingFiles.reduce(
      (sum, file) => sum + file.size,
      0,
    );

    // Calculate total size if new files are added
    const newFilesTotalSize = acceptedFiles.reduce(
      (sum, file) => sum + file.size,
      0,
    );
    const totalSize = existingTotalSize + newFilesTotalSize;

    if (totalSize > maxTotalSize) {
      toast.error(`Total file size exceeds ${maxFileSize}MB limit.`);
      return;
    }

    // Store files temporarily - they will be uploaded on submit
    const updatedFiles = [...existingFiles, ...acceptedFiles];
    handleFilesChange(updatedFiles);
  };

  const handleFilesChange = (newFiles: File[] | null) => {
    setFiles(newFiles);
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileChange,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    // Remove maxSize to allow validation in handleFileChange for total size
    disabled: mode === "view" || submitting || uploading,
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "view") return;

    // Validation
    const missingFields: string[] = [];
    if (!formData.equipmentId) missingFields.push("Equipment Id");
    if (!formData.equipmentName) missingFields.push("Equipment Name");
    if (!formData.category) missingFields.push("Category");
    if (!formData.subcategory) missingFields.push("Subcategory");
    if (!formData.serialNumber) missingFields.push("Serial Number");
    if (!formData.purchaseDate) missingFields.push("Purchase Date");
    if (!formData.warrantyExpiryDate)
      missingFields.push("Warranty Expiry Date");
    if (!formData.department) missingFields.push("Department");
    if (!formData.condition) missingFields.push("Condition");
    if (!formData.region) missingFields.push("Region");
    if (!formData.premises) missingFields.push("Premises");
    if (!formData.floor) missingFields.push("Floor");
    if (!formData.status) missingFields.push("Status");
    if (!formData.responsiblePerson) missingFields.push("Responsible Person");
    if (!formData.maintenanceScheduled)
      missingFields.push("Maintenance Scheduled");
    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`,
      );
      return;
    }

    // Date validation
    const today = new Date();
    const purchaseDate = new Date(formData.purchaseDate);
    const warrantyDate = new Date(formData.warrantyExpiryDate);

    if (purchaseDate > today) {
      toast.error("Purchase Date cannot be in the future");
      return;
    }

    if (warrantyDate < purchaseDate) {
      toast.error("Warranty Expiry Date must be after Purchase Date");
      return;
    }

    // Validate total file size (10MB limit)
    if (files && files.length > 0) {
      const maxTotalSize = maxFileSize * 1024 * 1024; // 10MB total limit in bytes
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      if (totalSize > maxTotalSize) {
        toast.error(
          `Total file size exceeds ${maxFileSize}MB limit. Please remove some files.`,
        );
        setUploadErrorMessage(
          "Total file size exceeds 10MB limit. Please remove some files.",
        );
        return;
      }
    }

    setSubmitting(true);
    setUploadErrorMessage("");

    let uploadedFileObjects: Array<{
      url: string;
      name: string;
      uploadedAt: string;
    }> = [];
    try {
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) {
            uploadedFileObjects.push({
              url: s3Url,
              name: file.name,
              uploadedAt: new Date().toISOString(),
            });
          } else {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }
    } catch (error) {
      setUploadErrorMessage(`File upload failed: ${(error as Error).message}`);
      setSubmitting(false);
      return;
    }

    try {
      let response;

      // Prepare existing attachments as file objects
      const existingAttachmentObjects = existingAttachments.map((a) => ({
        url: a.url || (typeof a === "string" ? a : ""),
        name:
          a.filename ||
          a.name ||
          a.originalName ||
          a.url?.split("/").pop() ||
          "Unknown file",
        uploadedAt: a.uploadedAt || new Date().toISOString(),
      }));

      const payload = {
        equipmentId: formData.equipmentId,
        equipmentName: formData.equipmentName,
        equipmentCategoryId:
          formData.equipmentCategoryId || equipmentCategoryId || "",
        category: formData.category, // Send categoryId
        subCategory: formData.subcategory, // Send subcategoryId
        serialNumber: formData.serialNumber,
        manufacturer: formData.manufacturer,
        supplier: formData.supplier,
        purchaseDate: formData.purchaseDate,
        warrantyExpiryDate: formData.warrantyExpiryDate,
        amcStatus: formData.amcStatus,
        department: formData.department,
        condition: formData.condition,
        status: formData.status,
        regionId: formData.region,
        responsiblePerson: formData.responsiblePerson,
        maintenanceScheduled: formData.maintenanceScheduled,
        premisesId: formData.premises,
        floor: formData.floor,
        room: formData.room,
        attachments: [...existingAttachmentObjects, ...uploadedFileObjects],
      };

      if (mode === "update") {
        if (!equipmentId) {
          toast.error("Equipment ID is required for update");
          return;
        }
        response = await updateEquipmentManagement(equipmentId, payload);
      } else {
        response = await createEquipmentManagement(payload);
      }

      const successMessage =
        mode === "update"
          ? "Equipment updated successfully"
          : "Equipment created successfully";
      toast.success(successMessage);

      if (onSuccess) onSuccess();
      handleClose();

      if (mode === "create") {
        setFormData({
          equipmentCategoryId: equipmentCategoryId,
          equipmentId: "",
          equipmentName: "",
          category: "",
          subcategory: "",
          serialNumber: "",
          manufacturer: "",
          supplier: "",
          purchaseDate: "",
          warrantyExpiryDate: "",
          amcStatus: "",
          department: "",
          condition: "",
          status: "",
          region: "",
          responsiblePerson: "",
          maintenanceScheduled: "",
          premises: "",
          floor: "",
          room: "",
        });
        setSelectedDepartments([]);
        setFiles(null);
        setExistingAttachments([]);
      }
    } catch (error) {
      const errorMessage =
        (error as any).response?.data?.message ||
        (mode === "update"
          ? "Failed to update equipment"
          : "Failed to create equipment");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (mode === "create") {
      setFormData({
        equipmentCategoryId: equipmentCategoryId,
        equipmentId: "",
        equipmentName: "",
        category: "",
        subcategory: "",
        serialNumber: "",
        manufacturer: "",
        supplier: "",
        purchaseDate: "",
        warrantyExpiryDate: "",
        amcStatus: "",
        department: "",
        condition: "",
        status: "",
        region: "",
        premises: "",
        responsiblePerson: "",
        maintenanceScheduled: "",
        floor: "",
        room: "",
      });
      setSelectedDepartments([]);
      setFiles(null);
      setExistingAttachments([]);
    }
    setShowQRCode(false);
    onClose();
  };

  const isReadOnly = mode === "view";
  const isDisabled = mode === "view" || submitting || uploading;

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-lg">Loading equipment data...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {(uploading || submitting) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">
              {uploading ? "Uploading files..." : "Submitting..."}
            </h3>
            {uploading && (
              <p className="text-sm text-gray-600">
                Progress: {uploadProgress}%
              </p>
            )}
          </div>
        </div>
      )}

      <div
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] overflow-hidden"
        onClick={handleClose}
      >
        <div
          className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-6xl max-h-[95vh] overflow-y-auto relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b z-10">
            <div className="flex justify-between items-center p-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900">
                  {mode === "create"
                    ? "Add New Equipment"
                    : mode === "update"
                      ? "Update Equipment"
                      : "View Equipment"}
                </h2>
              </div>
              {mode === "view" && (
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <XIcon size={24} />
                </button>
              )}
            </div>
          </div>

          <form id="addEquipmentForm" onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {/* Equipment ID - Auto-generated, read-only */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Equipment ID
                </label>
                <div className="flex">
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                    value={formData.equipmentId || "Generating..."}
                    disabled
                    readOnly
                  />
                  {formData.equipmentId && (
                    <button
                      type="button"
                      onClick={() => {
                        setQRCodeValue(generateQRCodeContent(formData));
                        setShowQRCode(true);
                      }}
                      className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      title="Generate QR Code"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Equipment Name */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Equipment Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Enter Equipment Name"
                  name="equipmentName"
                  value={formData.equipmentName}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Category</option>
                  {equipmentCategory.map((category: EquipmentCategoryType) => (
                    <option key={category._id} value={category._id}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subcategory */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Subcategory <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="subcategory"
                  value={formData.subcategory}
                  onChange={handleChange}
                  required
                  disabled={isDisabled || !formData.category}
                >
                  <option value="">Select Subcategory</option>
                  {filteredSubcategories.map(
                    (subcategory: EquipmentSubcategoryType) => (
                      <option key={subcategory._id} value={subcategory._id}>
                        {subcategory.subcategoryName}{" "}
                      </option>
                    ),
                  )}
                </select>
              </div>

              {/* Serial Number */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Serial Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Enter Serial Number"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                />
              </div>

              {/* Manufacturer */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Manufacturer
                </label>
                {mode === "view" ? (
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                    value={
                      manufacturerList.find(
                        (m) => m._id === formData.manufacturer,
                      )?.manufacturerName || formData.manufacturer
                    }
                    readOnly
                  />
                ) : (
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    disabled={isDisabled}
                  >
                    <option value="">Select Manufacturer</option>
                    {manufacturerList.map((manufacturer) => (
                      <option key={manufacturer._id} value={manufacturer._id}>
                        {manufacturer.manufacturerName}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Supplier/Vendor */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Supplier/Vendor
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleChange}
                  disabled={isDisabled || loadingSuppliers}
                >
                  <option value="">Select Supplier</option>
                  {loadingSuppliers ? (
                    <option value="" disabled>
                      Loading suppliers...
                    </option>
                  ) : (
                    suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Purchase Date */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Purchase Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* Warranty Expiry Date */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Warranty Expiry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="warrantyExpiryDate"
                  value={formData.warrantyExpiryDate}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  min={formData.purchaseDate}
                />
              </div>

              {/* AMC Status */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  AMC Status
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="amcStatus"
                  value={formData.amcStatus}
                  onChange={handleChange}
                  disabled={isDisabled}
                >
                  <option value="">Select AMC Status</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Department/Location */}
              <div className="col-span-2">
                <label className="block mb-1 text-black font-medium">
                  Department <span className="text-red-500">*</span>
                </label>
                <MultiSelectDropdown
                  options={departments.map((dept: any) => ({
                    label: `${dept.departmentId} - ${dept.departmentName}`,
                    value: dept._id,
                  }))}
                  selectedValues={selectedDepartments}
                  onChange={handleDepartmentSelectChange}
                  placeholder="Select departments..."
                />
                {selectedDepartments.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedDepartments.length} department(s) selected
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-black font-medium">
                  Region <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Region</option>
                  {regions.map((region) => (
                    <option key={region._id} value={region._id}>
                      {region.regionName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-black font-medium">
                  Premises <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="premises"
                  value={formData.premises}
                  onChange={handleChange}
                  required
                  disabled={isDisabled || !formData.region}
                >
                  <option value="">Select Premises</option>
                  {filteredPremises.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.premisesName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-black font-medium">
                  Floor <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="floor"
                  value={formData.floor}
                  onChange={handleChange}
                  required
                  disabled={isDisabled || !formData.premises}
                >
                  <option value="">Select Floor</option>
                  {floorOptions.map((floorOption) => (
                    <option key={floorOption} value={floorOption}>
                      {floorOption}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-black font-medium">
                  Room
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Enter Room Number/Name"
                  name="room"
                  value={formData.room}
                  onChange={handleChange}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                />
              </div>

              {/* Condition */}
              <div>
                <label className="block mb-1 text-black font-medium">
                  Condition <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Condition</option>
                  {conditionOptions.map((condition) => (
                    <option key={condition} value={condition}>
                      {condition}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-black font-medium">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="In Repair">In Repair</option>
                  <option value="Decommissioned">Decommissioned</option>
                  <option value="Spare">Spare</option>
                </select>
              </div>
              <div>
                <label className="block mb-1 text-black font-medium">
                  Responsible Person <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="responsiblePerson"
                  value={formData.responsiblePerson}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Responsible Person</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 text-black font-medium">
                  Maintenance Scheduled <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  name="maintenanceScheduled"
                  value={formData.maintenanceScheduled}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Maintenance Scheduled</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Annual">Annual</option>
                  <option value="One-Time">One-Time</option>
                </select>
              </div>
            </div>

            {/* File Upload Section */}
            <FileUpload
              mode={mode}
              files={files}
              onFilesChange={handleFilesChange}
              existingAttachments={existingAttachments}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onPreviewFile={handlePreviewFile}
              uploading={uploading}
              uploadProgress={uploadProgress}
              disabled={isDisabled}
              maxFileSizeMB={maxFileSize}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
            />
          </form>
          {mode !== "view" && (
            <div className="flex justify-end gap-4 p-4 bg-[#5A7192]  sticky bottom-0">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2 rounded-full bg-[#8497B0] text-white hover:bg-teal-600 transition"
                disabled={isDisabled}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="addEquipmentForm"
                className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
                disabled={isDisabled}
              >
                {submitting || uploading
                  ? mode === "update"
                    ? "UPDATING..."
                    : "SUBMITTING..."
                  : mode === "update"
                    ? "UPDATE"
                    : "SUBMIT"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-45">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button
                onClick={handleCloseDocumentViewer}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : fileurl.url ? (
                fileurl.type === "pdf" ? (
                  <div className="w-full h-full">
                    <PdfViewer fileUrl={fileurl.url} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <DocumentViewer file={fileurl.url} name={fileurl.name} />
                  </div>
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showQRCode && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Equipment QR Code</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XIcon size={24} />
              </button>
            </div>

            <div className="flex flex-col items-center">
              <div className="bg-white p-4 border border-gray-200 mb-4">
                <QRCode
                  value={qrCodeValue}
                  size={200}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>

              <p className="text-sm text-gray-600 mb-4 text-center">
                Scan this QR code to view equipment details
              </p>

              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    // Create a canvas element to download the QR code
                    const canvas = document.getElementById(
                      "qrcode-canvas",
                    ) as HTMLCanvasElement;
                    if (canvas) {
                      const pngUrl = canvas
                        .toDataURL("image/png")
                        .replace("image/png", "image/octet-stream");
                      const downloadLink = document.createElement("a");
                      downloadLink.href = pngUrl;
                      downloadLink.download = `${formData.equipmentId}_qrcode.png`;
                      document.body.appendChild(downloadLink);
                      downloadLink.click();
                      document.body.removeChild(downloadLink);
                    }
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-500"
                >
                  Download QR Code
                </button>

                <button
                  onClick={() => setShowQRCode(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Hidden canvas for downloading */}
            <div className="hidden">
              <canvas id="qrcode-canvas" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddEquipmentModal;
